
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatFeedbackInterface,
  FeedbackRepositoryInterface,
  TicketFeedbackInterface,
} from '@/services/QC-V2/feedback/interfaces';
import { ChatInterface, FilterRequestData, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import FeedbacksFilters from '@/components/qa/feedback/FeedbacksFilters.vue';
import FeedbackChatsTable from '@/components/qa/feedback/FeedbackChatsTable.vue';
import { TicketInterface } from '@/services/QC-V2/ticket/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import FeedbackTicketsTable from '@/components/qa/feedback/FeedbackTicketsTable.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'feedbackRepositoryV2',
    'profileRepository',
  ],
  components: {
    FeedbackTicketsTable,
    FeedbackChatsTable,
    FeedbacksFilters,
  },
})
export default class AllFeedbacks extends Vue {
  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private chatFeedbacks: ChatFeedbackInterface[] = [];
  private chats: ChatInterface[] = [];
  private totalChatFeedbacks: number = 0;
  private chatsPage: number = 1;
  private disabledChatIds: string[] = [];

  private ticketFeedbacks: TicketFeedbackInterface[] = [];
  private tickets: TicketInterface[] = [];
  private totalTicketFeedbacks: number = 0;
  private ticketsPage: number = 1;
  private disabledTicketIds: number[] = [];

  private profilesMap: ProfileMapInterface = {};

  private filter: FilterRequestData = {};

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      const [
        [chatFeedbacks, totalChatFeedbacks],
        [ticketFeedbacks, totalTicketFeedbacks],
      ] = await Promise.all([
        this.initializeChatFeedbacks(),
        this.initializeTicketFeedbacks(),
      ]);

      await this.fetchProfiles(chatFeedbacks, ticketFeedbacks);

      this.chatFeedbacks = chatFeedbacks;
      this.totalChatFeedbacks = totalChatFeedbacks;
      this.chats = this.chatFeedbacks.map((feedback) => feedback.chat);

      this.ticketFeedbacks = ticketFeedbacks;
      this.totalTicketFeedbacks = totalTicketFeedbacks;
      this.tickets = this.ticketFeedbacks.map((feedback) => feedback.ticket);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private initializeChatFeedbacks(): Promise<[ChatFeedbackInterface[], number]> {
    return this.feedbackRepositoryV2.getAllChatFeedbacks({
      ...this.filter,
      page: this.chatsPage,
    });
  }

  private initializeTicketFeedbacks(): Promise<[TicketFeedbackInterface[], number]> {
    return this.feedbackRepositoryV2.getAllTicketFeedbacks({
      ...this.filter,
      page: this.ticketsPage,
    });
  }

  private async filterFeedbacks(filter: FilterRequestData): Promise<void> {
    this.ticketsPage = 1;
    this.chatsPage = 1;
    this.filter = filter;
    await this.initialize();
  }

  private async getChatFeedback(id: number): Promise<void> {
    const route = this.$router.resolve({ name: 'chat_feedback', params: { id: String(id) } });
    window.open(route.href, '_blank');
  }

  private async changeChatsPage(page: number): Promise<void> {
    if (this.chatsPage === page) {
      return;
    }

    this.chatsPage = page;

    try {
      const [chatFeedbacks, totalChatFeedbacks] = await this.initializeChatFeedbacks();

      await this.fetchProfiles(chatFeedbacks, []);

      this.chatFeedbacks = chatFeedbacks;
      this.totalChatFeedbacks = totalChatFeedbacks;
      this.chats = this.chatFeedbacks.map((feedback) => feedback.chat);

    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getTicketFeedback(id: number): Promise<void> {
    const route = this.$router.resolve({ name: 'ticket_feedback', params: { id: String(id) } });
    window.open(route.href, '_blank');
  }

  private async changeTicketsPage(page: number): Promise<void> {
    if (this.ticketsPage === page) {
      return;
    }

    this.ticketsPage = page;

    try {
      const [ticketFeedbacks, totalTicketFeedbacks] = await this.initializeTicketFeedbacks();

      await this.fetchProfiles([], ticketFeedbacks);

      this.ticketFeedbacks = ticketFeedbacks;
      this.totalTicketFeedbacks = totalTicketFeedbacks;
      this.tickets = this.ticketFeedbacks.map((feedback) => feedback.ticket);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchProfiles(
    chatFeedbacks: ChatFeedbackInterface[],
    ticketFeedbacks: TicketFeedbackInterface[],
  ): Promise<void> {
    const ids: string[] = [];
    chatFeedbacks.forEach((escalation) => {
      if (!ids.includes(escalation.feedbackFrom)) {
        ids.push(escalation.feedbackFrom);
      }

      if (!ids.includes(escalation.feedbackTo)) {
        ids.push(escalation.feedbackTo);
      }
    });

    ticketFeedbacks.forEach((escalation) => {
      if (!ids.includes(escalation.feedbackFrom)) {
        ids.push(escalation.feedbackFrom);
      }

      if (!ids.includes(escalation.feedbackTo)) {
        ids.push(escalation.feedbackTo);
      }
    });


    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

}
